import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, EnumDecoder, field, NumberDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { DataValidator } from '@stamhoofd/utility';
import wordDictionary from './data/audit-log-words.js';
/**
 * The audit log dependes on a lot of other structures. To avoid creating circular dependencies
 * We inject them afterwards since these dependencies are only used at runtime
 */
export const AuditLogReplacementDependencies = {
    enumHelpers: [],
    uuidToName: (() => null),
};
export var AuditLogReplacementType;
(function (AuditLogReplacementType) {
    // Base
    AuditLogReplacementType["Key"] = "Key";
    AuditLogReplacementType["Array"] = "Array";
    AuditLogReplacementType["Uuid"] = "Uuid";
    AuditLogReplacementType["Color"] = "Color";
    AuditLogReplacementType["Image"] = "Image";
    AuditLogReplacementType["Html"] = "Html";
    AuditLogReplacementType["LongText"] = "LongText";
    // Objects
    AuditLogReplacementType["Member"] = "Member";
    AuditLogReplacementType["User"] = "User";
    AuditLogReplacementType["Organization"] = "Organization";
    AuditLogReplacementType["Group"] = "Group";
    AuditLogReplacementType["Event"] = "Event";
    AuditLogReplacementType["RegistrationPeriod"] = "RegistrationPeriod";
    AuditLogReplacementType["StripeAccount"] = "StripeAccount";
    AuditLogReplacementType["Webshop"] = "Webshop";
    AuditLogReplacementType["Order"] = "Order";
    AuditLogReplacementType["Payment"] = "Payment";
    AuditLogReplacementType["PlatformMembershipType"] = "PlatformMembershipType";
    AuditLogReplacementType["MemberResponsibility"] = "MemberResponsibility";
    AuditLogReplacementType["DocumentTemplate"] = "DocumentTemplate";
    AuditLogReplacementType["Email"] = "Email";
    AuditLogReplacementType["EmailAddress"] = "EmailAddress";
    AuditLogReplacementType["EmailTemplate"] = "EmailTemplate";
})(AuditLogReplacementType || (AuditLogReplacementType = {}));
export class AuditLogReplacement extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.value = '';
        this.description = '';
        this.values = [];
    }
    flatten() {
        if (this.type === AuditLogReplacementType.Array) {
            const cleanedValues = [];
            for (const v of this.values.flatMap(v => v.flatten())) {
                if (v.type === AuditLogReplacementType.Key) {
                    const last = cleanedValues[cleanedValues.length - 1];
                    if (last && last.type === AuditLogReplacementType.Key) {
                        if (last.value) {
                            last.value += '.';
                        }
                        last.value += v.value;
                        continue;
                    }
                }
                cleanedValues.push(v.clone());
            }
            return cleanedValues;
        }
        return [this];
    }
    prepend(add) {
        if (!add) {
            return this;
        }
        return AuditLogReplacement.array([...add.flatten(), this]);
    }
    append(add) {
        if (!add) {
            return this;
        }
        return AuditLogReplacement.array([this, ...add.flatten()]);
    }
    static join(...values) {
        return AuditLogReplacement.array(values);
    }
    static array(values) {
        const v = AuditLogReplacement.create({ values: values.flatMap(v => { var _a; return (_a = v === null || v === void 0 ? void 0 : v.flatten()) !== null && _a !== void 0 ? _a : []; }), type: AuditLogReplacementType.Array }).flatten();
        if (v.length === 1) {
            return v[0];
        }
        return AuditLogReplacement.create({ values: v, type: AuditLogReplacementType.Array });
    }
    static key(str) {
        if (!str) {
            return AuditLogReplacement.empty();
        }
        return AuditLogReplacement.create({ value: str, type: AuditLogReplacementType.Key });
    }
    static empty() {
        return AuditLogReplacement.array([]);
    }
    static uuid(id) {
        return AuditLogReplacement.create({
            id,
            value: AuditLogReplacementDependencies.uuidToName(id) || '',
            type: AuditLogReplacementType.Uuid,
        });
    }
    static string(str) {
        return AuditLogReplacement.create({ value: str });
    }
    static html(str, title) {
        return AuditLogReplacement.create({ value: str, type: AuditLogReplacementType.Html, description: title });
    }
    static longText(str, title) {
        return AuditLogReplacement.create({ value: str, type: AuditLogReplacementType.LongText, description: title });
    }
    toString() {
        if (this.type === AuditLogReplacementType.Key || this.type === AuditLogReplacementType.EmailTemplate) {
            return getAuditLogPatchKeyName(this.value);
        }
        if (this.type === AuditLogReplacementType.Uuid || (this.id && !this.value && DataValidator.isUuid(this.id))) {
            if (this.id && !this.value) {
                const name = AuditLogReplacementDependencies.uuidToName(this.id);
                if (name) {
                    return name;
                }
                return '';
            }
        }
        if (this.type === AuditLogReplacementType.Array) {
            return this.values.map(v => v.toString()).filter(v => !!v).join(' → ');
        }
        if (this.type === AuditLogReplacementType.Html) {
            return this.description || 'Bekijk inhoud';
        }
        if (this.type === AuditLogReplacementType.LongText) {
            return this.description || 'Bekijk inhoud';
        }
        if (this.type === AuditLogReplacementType.Image) {
            return 'Afbeelding';
        }
        return this.value;
    }
    toKey() {
        if (this.type === AuditLogReplacementType.Array) {
            return this.values.map(v => v.toKey()).filter(v => !!v).join('.');
        }
        return this.value;
    }
    lastValue() {
        if (this.type === AuditLogReplacementType.Array && this.values.length > 0) {
            return this.values[this.values.length - 1].lastValue();
        }
        if (this.type === AuditLogReplacementType.Key) {
            return this.value.split('.').pop() || '';
        }
        return this.value;
    }
}
__decorate([
    field({ field: 'v', decoder: StringDecoder, optional: true })
], AuditLogReplacement.prototype, "value", void 0);
__decorate([
    field({ field: 'd', decoder: StringDecoder, optional: true })
], AuditLogReplacement.prototype, "description", void 0);
__decorate([
    field({ field: 'a', decoder: new ArrayDecoder(AuditLogReplacement), optional: true })
], AuditLogReplacement.prototype, "values", void 0);
__decorate([
    field({ field: 'i', decoder: StringDecoder, optional: true })
], AuditLogReplacement.prototype, "id", void 0);
__decorate([
    field({ field: 't', decoder: new EnumDecoder(AuditLogReplacementType), optional: true })
], AuditLogReplacement.prototype, "type", void 0);
__decorate([
    field({ field: 'c', decoder: NumberDecoder, optional: true })
], AuditLogReplacement.prototype, "count", void 0);
export function getAuditLogPatchKeyName(key) {
    // Strip prefixes
    const stripPrefixes = ['settings.', 'meta.', 'privateMeta.', 'privateConfig.', 'config.', 'privateSettings.', 'details.', 'data.'];
    for (const prefix of stripPrefixes) {
        if (key.startsWith(prefix)) {
            key = key.substring(prefix.length);
        }
    }
    if (wordDictionary[key]) {
        return wordDictionary[key];
    }
    // Check first letter is a capital letter
    if (key.length > 1 && key[0] === key[0].toUpperCase()) {
        for (const helper of AuditLogReplacementDependencies.enumHelpers) {
            try {
                const result = helper(key);
                if (result && result !== key) {
                    return result;
                }
            }
            catch (e) {
                console.error(e);
            }
        }
    }
    if (key.includes('.')) {
        const splitted = key.split('.');
        if (splitted.length > 2) {
            const firstTwoWords = splitted.slice(0, 2).join('.');
            if (firstTwoWords !== getAuditLogPatchKeyName(firstTwoWords)) {
                return `${getAuditLogPatchKeyName(firstTwoWords)} → ${getAuditLogPatchKeyName(splitted.slice(2).join('.'))}`;
            }
        }
        const firstWord = splitted[0];
        const remaining = splitted.slice(1).join('.');
        return `${getAuditLogPatchKeyName(firstWord)} → ${getAuditLogPatchKeyName(remaining)}`;
    }
    if (key.length > 2 && key.endsWith('Id')) {
        // Strip id and try again
        return getAuditLogPatchKeyName(key.substring(0, key.length - 2));
    }
    // Replace camel case with spaces
    key = key.replace(/([a-z])([A-Z])/g, '$1 $2');
    // Replace _ case with spaces
    key = key.replace(/_+/g, ' ').trim();
    return key;
}
//# sourceMappingURL=AuditLogReplacement.js.map